/**
 * MockupHub - Central navigation for all UX/UI mockups
 *
 * One URL to access all mockups: ?mockup=hub
 *
 * Features:
 * - Sidebar navigation to switch between mockups
 * - Visual guide explaining enhancement markers
 * - Quick comparison between all proposed changes
 *
 * Created: December 4, 2025
 */

import React, { useState, useEffect } from 'react';

// Import all mockups
import OverviewTabMockup from './OverviewTabMockup';
import ThisMonthTabMockup from './ThisMonthTabMockup';
import LastMonthTabMockup from './LastMonthTabMockup';
import ThisWeekTabMockup from './ThisWeekTabMockup';
import LastWeekTabMockup from './LastWeekTabMockup';
import HistoricalTabMockup from './HistoricalTabMockup';

// Menu items configuration
const MENU_ITEMS = [
  { id: 'guide', label: 'How to Read Mockups', icon: '📖', isGuide: true },
  { id: 'divider1', isDivider: true },
  { id: 'overview', label: 'Overview', icon: '🏠' },
  { id: 'thismonth', label: 'This Month', icon: '📅' },
  { id: 'lastmonth', label: 'Last Month', icon: '📆' },
  { id: 'thisweek', label: 'This Week', icon: '📊' },
  { id: 'lastweek', label: 'Last Week', icon: '📈' },
  { id: 'divider2', isDivider: true },
  { id: 'historical-header', label: 'Historical Subtabs:', isHeader: true },
  { id: 'rep-vs-rep', label: 'Rep v Rep', icon: '👥', isHistorical: true },
  { id: 'single-rep', label: 'Single Rep Deep-Dive', icon: '👤', isHistorical: true },
  { id: 'team-snapshot', label: 'Team Snapshot', icon: '📋', isHistorical: true },
];

export default function MockupHub() {
  const [selectedMockup, setSelectedMockup] = useState('guide');

  useEffect(() => {
    document.title = '🎨 Mockup Hub - UX/UI Audit';
  }, []);

  // Render the selected mockup
  const renderMockup = () => {
    switch (selectedMockup) {
      case 'guide':
        return <EnhancementGuide />;
      case 'overview':
        return <OverviewTabMockup />;
      case 'thismonth':
        return <ThisMonthTabMockup />;
      case 'lastmonth':
        return <LastMonthTabMockup />;
      case 'thisweek':
        return <ThisWeekTabMockup />;
      case 'lastweek':
        return <LastWeekTabMockup />;
      case 'rep-vs-rep':
        return <HistoricalTabMockup initialSubtab="rep-vs-rep" />;
      case 'single-rep':
        return <HistoricalTabMockup initialSubtab="single-rep" />;
      case 'team-snapshot':
        return <HistoricalTabMockup initialSubtab="team-snapshot" />;
      default:
        return <EnhancementGuide />;
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar Navigation */}
      <nav style={{
        width: '260px',
        minWidth: '260px',
        backgroundColor: '#1e293b',
        color: 'white',
        padding: '20px 0',
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        overflowY: 'auto',
        boxShadow: '2px 0 10px rgba(0,0,0,0.2)'
      }}>
        {/* Header */}
        <div style={{
          padding: '0 20px 20px',
          borderBottom: '1px solid #334155',
          marginBottom: '16px'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>🎨</div>
          <h1 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>Mockup Hub</h1>
          <p style={{ fontSize: '12px', color: '#94a3b8', margin: '4px 0 0' }}>
            UX/UI Audit Enhancements
          </p>
        </div>

        {/* Menu Items */}
        <div style={{ padding: '0 12px' }}>
          {MENU_ITEMS.map((item) => {
            if (item.isDivider) {
              return (
                <div
                  key={item.id}
                  style={{
                    height: '1px',
                    backgroundColor: '#334155',
                    margin: '12px 8px'
                  }}
                />
              );
            }

            if (item.isHeader) {
              return (
                <div
                  key={item.id}
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '8px 12px 4px',
                    marginTop: '4px'
                  }}
                >
                  {item.label}
                </div>
              );
            }

            const isSelected = selectedMockup === item.id;
            const isGuide = item.isGuide;
            const isHistorical = item.isHistorical;

            return (
              <button
                key={item.id}
                onClick={() => setSelectedMockup(item.id)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: isHistorical ? '10px 12px 10px 24px' : '10px 12px',
                  marginBottom: '4px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: isSelected
                    ? (isGuide ? '#7c3aed' : '#3b82f6')
                    : 'transparent',
                  color: isSelected ? 'white' : '#cbd5e1',
                  fontSize: '14px',
                  fontWeight: isSelected ? 600 : 400,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = '#334155';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <span style={{ fontSize: '16px' }}>{item.icon}</span>
                <span>{item.label}</span>
                {isGuide && (
                  <span style={{
                    marginLeft: 'auto',
                    fontSize: '10px',
                    padding: '2px 6px',
                    backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : '#7c3aed',
                    borderRadius: '4px',
                    fontWeight: 600
                  }}>
                    START HERE
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '16px 20px',
          borderTop: '1px solid #334155',
          backgroundColor: '#1e293b'
        }}>
          <div style={{ fontSize: '11px', color: '#64748b' }}>
            Click any tab above to preview
          </div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
            Look for <span style={{ color: '#10b981' }}>green badges</span> marking changes
          </div>
        </div>
      </nav>

      {/* Main Content Area */}
      <main style={{
        flex: 1,
        marginLeft: '260px',
        backgroundColor: '#f1f5f9',
        minHeight: '100vh'
      }}>
        {renderMockup()}
      </main>
    </div>
  );
}

/**
 * Enhancement Guide - Explains how to read the mockups
 */
function EnhancementGuide() {
  return (
    <div style={{
      padding: '40px',
      maxWidth: '900px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '40px'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎨</div>
        <h1 style={{ fontSize: '32px', fontWeight: 700, margin: '0 0 8px', color: '#1e293b' }}>
          How to Read These Mockups
        </h1>
        <p style={{ fontSize: '16px', color: '#64748b', margin: 0 }}>
          Before viewing mockups, understand what the markers mean
        </p>
      </div>

      {/* Enhancement Markers Explanation */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        marginBottom: '24px'
      }}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, margin: '0 0 24px', color: '#1e293b' }}>
          Enhancement Markers You'll See
        </h2>

        {/* Marker 1 */}
        <div style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'flex-start',
          marginBottom: '24px',
          paddingBottom: '24px',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <div style={{
            padding: '6px 12px',
            backgroundColor: '#d1fae5',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#065f46',
            fontWeight: 600,
            whiteSpace: 'nowrap'
          }}>
            ✨ NEW
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: '4px', color: '#1e293b' }}>
              Brand New Feature
            </div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              This element doesn't exist in the current live dashboard. It's completely new.
            </div>
          </div>
        </div>

        {/* Marker 2 */}
        <div style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'flex-start',
          marginBottom: '24px',
          paddingBottom: '24px',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <div style={{
            padding: '6px 12px',
            backgroundColor: '#d1fae5',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#065f46',
            fontWeight: 600,
            whiteSpace: 'nowrap'
          }}>
            ✨ ENHANCED
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: '4px', color: '#1e293b' }}>
              Improved Existing Feature
            </div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              This feature exists in the live dashboard but has been redesigned or improved.
            </div>
          </div>
        </div>

        {/* Marker 3 */}
        <div style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'flex-start',
          marginBottom: '24px',
          paddingBottom: '24px',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <div style={{
            padding: '6px 12px',
            backgroundColor: '#d1fae5',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#065f46',
            fontWeight: 600,
            whiteSpace: 'nowrap'
          }}>
            ✨ MERGED
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: '4px', color: '#1e293b' }}>
              Combined Multiple Sections
            </div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              Multiple separate sections from the live dashboard have been combined into one for less scrolling.
            </div>
          </div>
        </div>

        {/* Marker 4 */}
        <div style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'flex-start',
          marginBottom: '24px',
          paddingBottom: '24px',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <div style={{
            padding: '6px 12px',
            backgroundColor: '#d1fae5',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#065f46',
            fontWeight: 600,
            whiteSpace: 'nowrap'
          }}>
            ✨ CONSOLIDATED
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: '4px', color: '#1e293b' }}>
              Multiple Charts Combined
            </div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              Multiple separate charts have been combined into one chart for easier comparison.
            </div>
          </div>
        </div>

        {/* Marker 5 */}
        <div style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'flex-start'
        }}>
          <div style={{
            padding: '6px 12px',
            backgroundColor: '#d1fae5',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#065f46',
            fontWeight: 600,
            whiteSpace: 'nowrap'
          }}>
            ✨ Collapsible
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: '4px', color: '#1e293b' }}>
              Click to Collapse/Expand
            </div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              This section can be collapsed by clicking its header. Try it - click the section title!
            </div>
          </div>
        </div>
      </div>

      {/* Other Visual Cues */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        marginBottom: '24px'
      }}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, margin: '0 0 24px', color: '#1e293b' }}>
          Other Visual Cues
        </h2>

        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontWeight: 600, marginBottom: '8px', color: '#1e293b' }}>
            🟡 Yellow Banner at Top
          </div>
          <div style={{
            padding: '8px 16px',
            backgroundColor: '#fef3c7',
            border: '2px solid #f59e0b',
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            <strong style={{ color: '#92400e' }}>🎨 MOCKUP MODE</strong>
            <span style={{ color: '#92400e' }}> - This tells you you're viewing a mockup, not the live dashboard</span>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontWeight: 600, marginBottom: '8px', color: '#1e293b' }}>
            🔵 Enhancement Legend at Bottom
          </div>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            Every mockup has a blue box at the very bottom listing ALL enhancements on that page.
          </div>
        </div>

        <div>
          <div style={{ fontWeight: 600, marginBottom: '8px', color: '#1e293b' }}>
            ▼ Arrow on Section Headers
          </div>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            If you see a ▼ arrow on a section header, click it to collapse that section. Click again to expand.
          </div>
        </div>
      </div>

      {/* Quick Start */}
      <div style={{
        backgroundColor: '#eff6ff',
        borderRadius: '16px',
        padding: '32px',
        border: '2px solid #3b82f6'
      }}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, margin: '0 0 16px', color: '#1e40af' }}>
          Ready to Start?
        </h2>
        <p style={{ fontSize: '14px', color: '#1e40af', margin: '0 0 16px' }}>
          Click any tab in the left sidebar to view that mockup. Recommended order:
        </p>
        <ol style={{ margin: 0, paddingLeft: '20px', color: '#1e40af', fontSize: '14px' }}>
          <li style={{ marginBottom: '8px' }}><strong>Overview</strong> - Simplest changes (sparklines, icons)</li>
          <li style={{ marginBottom: '8px' }}><strong>This Month</strong> - See collapsible sections, sticky nav</li>
          <li style={{ marginBottom: '8px' }}><strong>This Week</strong> - See urgency redesign, trend chart</li>
          <li style={{ marginBottom: '8px' }}><strong>Historical tabs</strong> - See major reorganization</li>
        </ol>
      </div>
    </div>
  );
}
